/**
 * Oportunidades: os mais vendidos do Mercado Livre cruzados com o catálogo da loja.
 * A pergunta que a tela responde: o que vende muito no Mercado Livre e eu não tenho (ou tenho e não anuncio)?
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Plus, RefreshCw, Search } from 'lucide-react';
import type { MarketCategory, MarketRoot, OpportunityPage, OpportunitySituation } from '@berytus/contracts';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag, Thumb } from '../components/ui.tsx';
import { titleOf } from './Catalog.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';

const SITUATION: Record<OpportunitySituation, { label: string; tone: string; kpi: string; note: string }> = {
  missing: { label: 'Não tenho', tone: 'err', kpi: 'Não tenho no catálogo', note: 'oportunidade de compra' },
  unlisted: { label: 'Tenho, sem anúncio', tone: 'warn', kpi: 'Tenho e não anuncio', note: 'oportunidade de anunciar' },
  listed: { label: 'Anuncio', tone: 'ok', kpi: 'Já anuncio', note: 'compare preço e posição' },
  unknown: { label: 'Sem EAN no ML', tone: 'off', kpi: 'Sem EAN no Mercado Livre', note: 'conferir pelo nome' },
};

const PHASE: Record<string, string> = { categorias: 'lendo categorias', ranking: 'lendo ranking', detalhes: 'lendo produtos', fim: 'concluindo' };

const COLS: [string, number, string?][] = [
  ['Pos.', 64, 'num'], ['', 56], ['Produto no Mercado Livre', 0], ['Categoria', 220], ['Preço ML', 104, 'num'],
  ['Catálogo', 104], ['Situação', 150], ['Meu produto', 240],
];

export function Opportunities() {
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const qc = useQueryClient();

  const roots = useQuery({
    queryKey: ['meli', 'market-roots'],
    queryFn: () => api<MarketRoot[]>('/channels/mercadolivre/market/roots'),
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'queued' || r.status === 'running') ? 3000 : false),
  });
  const tracked = (roots.data ?? []).filter((r) => r.tracked);
  const root = params.get('root') ?? tracked[0]?.id ?? null;
  const busy = tracked.some((r) => r.status === 'queued' || r.status === 'running');

  const refresh = useMutation({
    mutationFn: (id: string) => api(`/channels/mercadolivre/market/roots/${id}/refresh`, { method: 'POST' }),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['meli', 'market-roots'] });
      set({ root: id, category: null, page: null });
    },
  });

  const cats = useQuery({
    queryKey: ['meli', 'opp-cats', root],
    queryFn: () => api<MarketCategory[]>(`/channels/mercadolivre/opportunities/categories?root=${root}`),
    enabled: !!root,
  });

  const page = Number(params.get('page') ?? 1);
  const pageSize = Number(params.get('size') ?? 50);
  const query = new URLSearchParams();
  if (root) query.set('root', root);
  for (const k of ['category', 'situation', 'q']) if (params.get(k)) query.set(k, params.get(k)!);
  const top = params.get('top') ?? '100';
  query.set('top', top);
  query.set('page', String(page));
  query.set('pageSize', String(pageSize));
  const list = useQuery({
    queryKey: ['meli', 'opportunities', query.toString()],
    queryFn: () => api<OpportunityPage>(`/channels/mercadolivre/opportunities?${query}`),
    enabled: !!root,
    placeholderData: keepPreviousData,
    refetchInterval: busy ? 8000 : false,
  });
  const sum = list.data?.summary;
  const situation = params.get('situation') ?? 'all';
  const rows = list.data?.items ?? [];

  return (
    <>
      <Head title="Oportunidades">
        <p>
          O Mercado Livre informa os 20 mais vendidos de cada subcategoria; a Berytus junta esses rankings e mostra
          primeiro os 1º lugares das subcategorias maiores. Cruzado com o seu catálogo pelo EAN: comece pelos que você não tem.
        </p>
      </Head>

      {roots.isPending ? <Loading /> : roots.error ? <ErrorBox error={roots.error} /> : (
        <div className="stack-y">
          <Roots roots={roots.data} current={root} onPick={(id) => set({ root: id, category: null, page: null })} onRefresh={(id) => refresh.mutate(id)} refreshing={refresh.isPending} />
          {refresh.error && <ErrorBox error={refresh.error} />}

          {!root ? null : (
            <>
              {sum && (
                <div className="kpis">
                  {(['missing', 'unlisted', 'listed', 'unknown'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`kpi${situation === k ? ' active' : ''}`}
                      style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                      onClick={() => set({ situation: situation === k ? null : k, page: null })}
                      aria-pressed={situation === k}
                    >
                      <span className="k-label">{SITUATION[k].kpi}</span>
                      <span className="k-value" style={k === 'missing' ? { color: 'var(--err)' } : undefined}>{int(sum[k])}</span>
                      <span className="k-note">{SITUATION[k].note}</span>
                    </button>
                  ))}
                </div>
              )}

              <div>
                <div className="toolbar">
                  <label className="search">
                    <Search size={16} />
                    <span className="sr-only">Buscar</span>
                    <input className="input" type="search" placeholder="Buscar por nome, marca ou EAN" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </label>
                  <select className="select" aria-label="Categoria do Mercado Livre" value={params.get('category') ?? ''} onChange={(e) => set({ category: e.target.value || null, page: null })} style={{ maxWidth: 320 }}>
                    <option value="">Todas as categorias</option>
                    {cats.data?.filter((c) => c.depth > 1).map((c) => (
                      <option key={c.id} value={c.id}>{' '.repeat(Math.max(0, c.depth - 2) * 2)}{c.name} ({int(c.ranked)})</option>
                    ))}
                  </select>
                  <select className="select" aria-label="Situação" value={situation} onChange={(e) => set({ situation: e.target.value === 'all' ? null : e.target.value, page: null })}>
                    <option value="all">Qualquer situação</option>
                    {(['missing', 'unlisted', 'listed', 'unknown'] as const).map((k) => <option key={k} value={k}>{SITUATION[k].label}</option>)}
                  </select>
                  <select className="select" aria-label="Recorte do ranking" value={top} onChange={(e) => set({ top: e.target.value === '100' ? null : e.target.value, page: null })}>
                    <option value="100">Top 100</option>
                    <option value="300">Top 300</option>
                    <option value="all">Todo o ranking</option>
                  </select>
                  <select className="select" aria-label="Por página" value={pageSize} onChange={(e) => set({ size: e.target.value === '50' ? null : e.target.value, page: null })}>
                    <option value="50">50 por página</option>
                    <option value="100">100 por página</option>
                  </select>
                </div>

                <div className="panel flush">
                  {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
                    : !list.isPending && rows.length === 0 ? (
                      <div className="empty">
                        <b>{busy ? 'Lendo os mais vendidos…' : 'Nada por aqui'}</b>
                        {busy ? 'A primeira leitura de uma categoria grande leva alguns minutos. A lista aparece conforme o ranking é lido.' : 'Mude a busca ou os filtros, ou atualize a categoria.'}
                      </div>
                    ) : (
                      <>
                        <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                          <table className="grid" style={{ width: '100%', minWidth: COLS.reduce((n, c) => n + (c[1] || 260), 0) }}>
                            <colgroup>{COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                            <thead><tr>{COLS.map(([l, , c], i) => <th key={i} className={c}>{l}</th>)}</tr></thead>
                            <tbody>
                              {list.isPending
                                ? Array.from({ length: 10 }, (_, i) => <tr key={i}>{COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                                : rows.map((r) => (
                                    <tr key={`${r.categoryId}-${r.position}`} style={{ cursor: 'default' }}>
                                      <td className="num"><b>{r.position}º</b></td>
                                      <td><Thumb src={r.thumbnail} alt="" /></td>
                                      <td>
                                        <div className="cell-2">
                                          <span title={r.title ?? undefined} className={r.title ? '' : 'untitled'}>{r.title ?? r.extId}</span>
                                          <span className="sub">
                                            {[r.brand, r.gtin].filter(Boolean).join(' · ')}
                                            {(r.brand || r.gtin) && ' · '}
                                            {r.permalink ? <a className="ext" href={r.permalink} target="_blank" rel="noreferrer">{r.extId} <ExternalLink size={11} /></a> : r.extId}
                                          </span>
                                        </div>
                                      </td>
                                      <td title={r.categoryPath}>{r.categoryPath.split(' > ').slice(-1)[0]}</td>
                                      <td className="num">{brl(r.price)}</td>
                                      <td>{r.isCatalog ? <span className="yes">Sim</span> : <span className="no">Não</span>}</td>
                                      <td><Tag tone={SITUATION[r.situation].tone}>{SITUATION[r.situation].label}</Tag></td>
                                      <td>
                                        {r.product ? (
                                          <div className="cell-2">
                                            <Link to={`/catalogo/${r.product.id}`} title={r.product.title ?? undefined}>{titleOf(r.product)}</Link>
                                            <span className="sub">
                                              SKU {r.product.sku} · {int(r.product.available)} disp.
                                              {r.listing && <> · {r.listing.permalink ? <a className="ext" href={r.listing.permalink} target="_blank" rel="noreferrer">{r.listing.externalId} <ExternalLink size={11} /></a> : r.listing.externalId} {brl(r.listing.price)}</>}
                                            </span>
                                          </div>
                                        ) : r.listing ? (
                                          <span className="small">{r.listing.externalId} · {brl(r.listing.price)}</span>
                                        ) : (
                                          <span className="small muted">—</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="cards">
                          {rows.map((r) => (
                            <div key={`${r.categoryId}-${r.position}`} className="card-row">
                              <Thumb src={r.thumbnail} alt="" />
                              <div style={{ minWidth: 0 }}>
                                <div className="t">{r.position}º · {r.title ?? r.extId}</div>
                                <div className="m">{r.categoryPath.split(' > ').slice(-1)[0]}{r.gtin && <> · {r.gtin}</>}</div>
                                <div style={{ marginTop: 4 }}><Tag tone={SITUATION[r.situation].tone}>{SITUATION[r.situation].label}</Tag></div>
                              </div>
                              <div className="r">{brl(r.price)}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  {list.data && <Pager total={list.data.total} page={page} pageSize={list.data.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** Categorias raiz acompanhadas (escopo da tela) e a escolha de outras para acompanhar. */
function Roots({ roots, current, onPick, onRefresh, refreshing }: {
  roots: MarketRoot[];
  current: string | null;
  onPick: (id: string) => void;
  onRefresh: (id: string) => void;
  refreshing: boolean;
}) {
  const tracked = roots.filter((r) => r.tracked);
  const others = roots.filter((r) => !r.tracked);
  const [adding, setAdding] = useState('');
  const cur = tracked.find((r) => r.id === current);

  if (tracked.length === 0) {
    const suggested = others.filter((r) => r.suggested);
    return (
      <section className="panel stack-y" style={{ gap: 12 }}>
        <h2 style={{ margin: 0 }}>Escolha as categorias para acompanhar</h2>
        <p className="small muted" style={{ margin: 0 }}>
          A Berytus lê os mais vendidos de todas as subcategorias uma vez; depois, você atualiza quando quiser. A primeira leitura leva alguns minutos.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(suggested.length ? suggested : others.slice(0, 6)).map((r) => (
            <button key={r.id} type="button" className="btn" disabled={refreshing} onClick={() => onRefresh(r.id)}>
              <Plus size={14} /> {r.name}
            </button>
          ))}
        </div>
        <AddRoot others={others} adding={adding} setAdding={setAdding} onAdd={onRefresh} disabled={refreshing} />
      </section>
    );
  }

  return (
    <section className="panel" style={{ padding: 14, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="seg" role="group" aria-label="Categoria acompanhada">
          {tracked.map((r) => (
            <button key={r.id} type="button" aria-pressed={r.id === current} onClick={() => onPick(r.id)}>
              {r.name} <span className="muted">{int(r.ranked)}</span>
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <AddRoot others={others} adding={adding} setAdding={setAdding} onAdd={onRefresh} disabled={refreshing} />
      </div>
      {cur && (
        <div className="small muted" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {cur.status === 'queued' || cur.status === 'running' ? (
            <span>
              <Tag tone="info">Atualizando</Tag>{' '}
              {cur.progress?.phase ? PHASE[cur.progress.phase] ?? cur.progress.phase : 'na fila'}
              {cur.progress?.categories ? ` · ${int(cur.progress.categories)} categorias` : ''}
              {cur.progress?.ranked ? ` · ${int(cur.progress.ranked)} posições` : ''}
            </span>
          ) : cur.status === 'failed' ? (
            <span style={{ color: 'var(--err)' }}>A última atualização falhou: {cur.lastError}</span>
          ) : (
            <span>Atualizado em {dateTime(cur.refreshedAt)} · {int(cur.progress?.leaves ?? 0)} subcategorias · atualize quando quiser</span>
          )}
          <button className="btn sm quiet" type="button" disabled={refreshing || cur.status === 'queued' || cur.status === 'running'} onClick={() => onRefresh(cur.id)}>
            <RefreshCw size={13} /> Atualizar agora
          </button>
        </div>
      )}
    </section>
  );
}

function AddRoot({ others, adding, setAdding, onAdd, disabled }: {
  others: MarketRoot[];
  adding: string;
  setAdding: (v: string) => void;
  onAdd: (id: string) => void;
  disabled: boolean;
}) {
  if (!others.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <select className="select" aria-label="Acompanhar outra categoria" value={adding} onChange={(e) => setAdding(e.target.value)} style={{ height: 32 }}>
        <option value="">Acompanhar outra categoria…</option>
        {others.map((r) => <option key={r.id} value={r.id}>{r.name}{r.suggested ? ' (sugerida)' : ''}</option>)}
      </select>
      <button className="btn sm" type="button" disabled={!adding || disabled} onClick={() => { onAdd(adding); setAdding(''); }}>
        <Plus size={14} /> Acompanhar
      </button>
    </div>
  );
}
